import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Printer, FilePdf, FileXls, Package, Stack, Scales,
  CurrencyInr, Calendar, Buildings, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

export default function ProductionPlanPreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { guard } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/production/v2/plans/${id}`);
        setPlan(data);
      } catch {
        toast.error("Could not load plan");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const download = async (kind) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/production/v2/plans/${id}/export/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_Production_Plan_${(plan?.name || "plan").replace(/[^a-z0-9]+/gi, "_")}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const dlExcel = guard(() => download("excel"), "download Excel");
  const dlPdf = guard(() => download("pdf"), "download PDF");

  if (loading) {
    return (
      <div className="app-shell">
        <Header />
        <main className="max-w-7xl mx-auto px-5 py-10 text-center text-sm text-[#4B5563]">Loading plan...</main>
        <Footer />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="app-shell">
        <Header />
        <main className="max-w-7xl mx-auto px-5 py-10 text-center">
          <p className="text-sm text-[#4B5563]">Plan not found.</p>
          <Button onClick={() => navigate("/production")} className="mt-4">Back to Production Plan</Button>
        </main>
        <Footer />
      </div>
    );
  }

  const t = plan.totals || {};

  return (
    <div className="app-shell">
      <Header />

      {/* Action bar (hidden in print) */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white print:hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
                <Package size={14} weight="fill" /> Production Plan Preview
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-medium mt-2 leading-tight">
                {plan.name}
              </h1>
              <p className="text-white/75 text-sm mt-2">
                Print-ready snapshot — for the kitchen, the production manager, or your records.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => navigate(`/production?plan=${plan.id}`)}
                variant="outline"
                className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-11"
                data-testid="preview-back-btn"
              >
                <ArrowLeft size={16} weight="bold" className="mr-2" /> Edit
              </Button>
              <Button
                onClick={() => window.print()}
                variant="outline"
                className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-11"
                data-testid="preview-print-btn"
              >
                <Printer size={16} weight="bold" className="mr-2" /> Print
              </Button>
              <Button
                onClick={dlExcel}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11"
                data-testid="preview-excel-btn"
              >
                <FileXls size={16} weight="bold" className="mr-2" /> Excel
              </Button>
              <Button
                onClick={dlPdf}
                className="bg-white hover:bg-[#FDF9EE] text-[#0A3B24] font-semibold h-11 border border-[#D4AF37]"
                data-testid="preview-pdf-btn"
              >
                <FilePdf size={16} weight="bold" className="mr-2" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-6 print:py-2 print:space-y-3">
        {/* Print-only title */}
        <div className="hidden print:block">
          <h1 className="font-display text-3xl text-[#0A3B24]">KOSTRA — Production Plan</h1>
          <h2 className="font-display text-xl text-[#0A3B24] mt-1">{plan.name}</h2>
        </div>

        {/* Section A: Production Details */}
        <section className="kostra-card p-5" data-testid="preview-section-a">
          <SectionHeader letter="A" title="Production Details" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-3">
            <MetaCell icon={Calendar} label="Production Date" value={plan.production_date || "—"} />
            <MetaCell icon={Buildings} label="Production For" value={plan.production_for || "—"} />
            <MetaCell icon={Buildings} label="Outlet" value={plan.outlet || "—"} />
            <MetaCell icon={Buildings} label="Brand" value={plan.brand || "—"} />
            <MetaCell icon={Calendar} label="Prepared By" value={plan.prepared_by || "—"} />
          </div>
          {plan.notes && (
            <div className="mt-4 pt-3 border-t border-[#F1F2F4]">
              <div className="kostra-data-label mb-1">Notes</div>
              <p className="text-sm text-[#4B5563]">{plan.notes}</p>
            </div>
          )}
        </section>

        {/* KPI strip */}
        <section className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="preview-totals">
          <BigStat label="Total Portions" value={t.total_portions || 0} icon={Stack} />
          <BigStat label="Total Weight" value={`${(t.total_weight_kg || 0).toFixed(3)} kg`} icon={Scales} accent="#D4AF37" />
          <BigStat label="Total Cost" value={fmtINR(t.total_cost || 0)} icon={CurrencyInr} accent="#0A3B24" highlight />
        </section>

        {/* Section B: Recipes Planned */}
        <section className="kostra-card p-5 md:p-6" data-testid="preview-section-b">
          <SectionHeader letter="B" title="Recipes Planned" />
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Recipe</th>
                  <th className="py-2 pr-2">Category</th>
                  <th className="py-2 pr-2 text-right">Net Wt / Port.</th>
                  <th className="py-2 pr-2 text-right">Cost / Port.</th>
                  <th className="py-2 pr-2 text-right">Cost / Kg</th>
                  <th className="py-2 pr-2 text-center">Mode</th>
                  <th className="py-2 pr-2 text-right">Target</th>
                  <th className="py-2 pr-2 text-center">Waste %</th>
                  <th className="py-2 pr-2 text-right">Produced</th>
                  <th className="py-2 pr-2 text-right">Weight</th>
                  <th className="py-2 pl-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {(plan.items || []).map((it, i) => (
                  <tr key={it.id || i} className="border-b border-[#F1F2F4]" data-testid={`preview-row-${i}`}>
                    <td className="py-2 pr-2 font-mono-num text-[#4B5563]">{i + 1}</td>
                    <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{it.recipe_name}</td>
                    <td className="py-2 pr-2 text-[#4B5563]">{it.category || "—"}</td>
                    <td className="py-2 pr-2 text-right font-mono-num">{(it.net_weight_per_portion_g || 0).toFixed(0)} g</td>
                    <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(it.cost_per_portion || 0)}</td>
                    <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(it.cost_per_kg || 0)}</td>
                    <td className="py-2 pr-2 text-center text-[11px] uppercase tracking-wider text-[#4B5563]">
                      {it.mode === "weight" ? "Weight" : "Portions"}
                    </td>
                    <td className="py-2 pr-2 text-right font-mono-num">
                      {it.mode === "weight"
                        ? `${(it.target_total_weight_kg || 0).toFixed(3)} kg`
                        : `${Math.round(it.target_portions || 0)} port.`}
                    </td>
                    <td className="py-2 pr-2 text-center font-mono-num">{(it.waste_pct || 0).toFixed(0)}%</td>
                    <td className="py-2 pr-2 text-right font-mono-num font-semibold">{it.effective_portions || 0}</td>
                    <td className="py-2 pr-2 text-right font-mono-num">{(it.total_weight_kg || 0).toFixed(3)} kg</td>
                    <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(it.total_cost || 0)}</td>
                  </tr>
                ))}
                <tr className="bg-[#0A3B24] text-white font-bold">
                  <td className="py-3 pr-2" colSpan={9}>TOTAL</td>
                  <td className="py-3 pr-2 text-right font-mono-num">{t.total_portions || 0}</td>
                  <td className="py-3 pr-2 text-right font-mono-num">{(t.total_weight_kg || 0).toFixed(3)} kg</td>
                  <td className="py-3 pl-2 text-right font-mono-num">{fmtINR(t.total_cost || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section C: Ingredient Requirement Sheet */}
        {(plan.ingredient_requirements?.length || 0) > 0 && (
          <section className="kostra-card p-5 md:p-6" data-testid="preview-section-c">
            <SectionHeader letter="C" title="Ingredient Requirement Sheet" />
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-2">Ingredient</th>
                    <th className="py-2 pr-2">Category</th>
                    <th className="py-2 pr-2 text-right">Qty</th>
                    <th className="py-2 pr-2 text-right">Rate / Kg</th>
                    <th className="py-2 pl-2 text-right">Planned Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.ingredient_requirements.map((ing, i) => (
                    <tr key={ing.key || i} className="border-b border-[#F1F2F4]" data-testid={`preview-req-row-${i}`}>
                      <td className="py-2 pr-2 font-mono-num text-[#4B5563]">{i + 1}</td>
                      <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{ing.name}</td>
                      <td className="py-2 pr-2 text-[#4B5563]">{ing.category}</td>
                      <td className="py-2 pr-2 text-right font-mono-num">{ing.total_required_kg.toFixed(3)} kg</td>
                      <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(ing.rate_per_kg)}</td>
                      <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(ing.planned_cost)}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#0A3B24] text-white font-bold">
                    <td className="py-3 pr-2" colSpan={5}>TOTAL PLANNED INGREDIENT COST</td>
                    <td className="py-3 pl-2 text-right font-mono-num">{fmtINR(plan.requirement_totals?.planned_cost || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Section D: Purchase Requisition */}
        {(plan.ingredient_requirements || []).some((x) => (x.purchase_qty_kg || 0) > 0) && (
          <section className="kostra-card p-5 md:p-6" data-testid="preview-section-d">
            <SectionHeader letter="D" title="Purchase Requisition" />
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                    <th className="py-2 pr-2">Ingredient</th>
                    <th className="py-2 pr-2 text-right">Required</th>
                    <th className="py-2 pr-2 text-right">Available</th>
                    <th className="py-2 pr-2 text-right">Purchase Qty</th>
                    <th className="py-2 pr-2 text-right">Rate</th>
                    <th className="py-2 pl-2 text-right">Purchase Value</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.ingredient_requirements
                    .filter((x) => (x.purchase_qty_kg || 0) > 0)
                    .map((ing, i) => (
                      <tr key={ing.key || i} className="border-b border-[#F1F2F4]" data-testid={`preview-pr-row-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{ing.name}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{ing.total_required_kg.toFixed(3)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num text-[#4B5563]">{ing.available_stock_kg.toFixed(3)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num font-bold text-[#DC2626]">{ing.purchase_qty_kg.toFixed(3)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(ing.rate_per_kg)}</td>
                        <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(ing.purchase_value)}</td>
                      </tr>
                    ))}
                  <tr className="bg-[#D4AF37] text-[#0A3B24] font-bold">
                    <td className="py-3 pr-2" colSpan={5}>TOTAL PURCHASE VALUE</td>
                    <td className="py-3 pl-2 text-right font-mono-num">{fmtINR(plan.requirement_totals?.purchase_value || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Section E + F: Actual Spend + Variance */}
        {(plan.ingredient_requirements || []).some((x) => (x.actual_cost || 0) > 0) && (
          <section className="kostra-card p-5 md:p-6" data-testid="preview-section-ef">
            <SectionHeader letter="E + F" title="Actual Spend & Variance" />
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                    <th className="py-2 pr-2">Ingredient</th>
                    <th className="py-2 pr-2 text-right">Planned Cost</th>
                    <th className="py-2 pr-2 text-right">Actual Cost</th>
                    <th className="py-2 pr-2 text-right">Variance</th>
                    <th className="py-2 pl-2 text-right">Variance %</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.ingredient_requirements
                    .filter((x) => (x.actual_cost || 0) > 0)
                    .map((ing, i) => (
                      <tr key={ing.key || i} className="border-b border-[#F1F2F4]" data-testid={`preview-var-row-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{ing.name}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(ing.planned_cost)}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(ing.actual_cost)}</td>
                        <td className="py-2 pr-2 text-right font-mono-num font-bold"
                            style={{ color: ing.variance > 0 ? "#DC2626" : ing.variance < 0 ? "#16A34A" : "#4B5563" }}>
                          {ing.variance > 0 ? "+" : ""}{fmtINR(ing.variance)}
                        </td>
                        <td className="py-2 pl-2 text-right font-mono-num">
                          {ing.variance_pct > 0 ? "+" : ""}{ing.variance_pct.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  <tr className="bg-[#0A3B24] text-white font-bold">
                    <td className="py-3 pr-2">TOTAL</td>
                    <td className="py-3 pr-2 text-right font-mono-num">{fmtINR(plan.requirement_totals?.planned_cost || 0)}</td>
                    <td className="py-3 pr-2 text-right font-mono-num">{fmtINR(plan.requirement_totals?.actual_cost || 0)}</td>
                    <td className="py-3 pr-2 text-right font-mono-num">
                      {(plan.requirement_totals?.variance || 0) > 0 ? "+" : ""}{fmtINR(plan.requirement_totals?.variance || 0)}
                    </td>
                    <td className="py-3 pl-2 text-right font-mono-num">
                      {(plan.requirement_totals?.variance_pct || 0) > 0 ? "+" : ""}
                      {(plan.requirement_totals?.variance_pct || 0).toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Section G: Summary */}
        {(plan.ingredient_requirements?.length || 0) > 0 && (
          <section className="kostra-card p-5 md:p-6" data-testid="preview-section-g">
            <SectionHeader letter="G" title="Summary" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <SummaryCell label="Total Portions Planned" value={t.total_portions || 0} />
              <SummaryCell label="Total Production Weight" value={`${(t.total_weight_kg || 0).toFixed(3)} kg`} />
              <SummaryCell label="Planned Production Cost" value={fmtINR(t.total_cost || 0)} />
              <SummaryCell label="Planned Ingredient Cost" value={fmtINR(plan.requirement_totals?.planned_cost || 0)} />
              <SummaryCell label="Actual Spend" value={fmtINR(plan.requirement_totals?.actual_cost || 0)} accent="#0E5232" />
              <SummaryCell label="Cost Variance"
                  value={`${(plan.requirement_totals?.variance || 0) > 0 ? "+" : ""}${fmtINR(plan.requirement_totals?.variance || 0)}`}
                  accent={(plan.requirement_totals?.variance || 0) > 0 ? "#DC2626" : "#16A34A"} />
              <SummaryCell label="Variance %"
                  value={`${(plan.requirement_totals?.variance_pct || 0) > 0 ? "+" : ""}${(plan.requirement_totals?.variance_pct || 0).toFixed(2)}%`}
                  accent={(plan.requirement_totals?.variance_pct || 0) > 0 ? "#DC2626" : "#16A34A"} />
              <SummaryCell label="Purchase Requirement Value" value={fmtINR(plan.requirement_totals?.purchase_value || 0)} accent="#D4AF37" />
            </div>
          </section>
        )}

        <div className="text-[10px] text-[#4B5563] uppercase tracking-widest text-center pt-4 print:pt-2">
          KOSTRA · The Food Costing Expert by Anupam
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SectionHeader({ letter, title }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-[#0A3B24] text-[#D4AF37] flex items-center justify-center font-display font-bold text-base">
        {letter}
      </div>
      <h3 className="kostra-section-title text-lg">{title}</h3>
    </div>
  );
}

function SummaryCell({ label, value, accent = "#0A3B24" }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-[#FDF9EE]/30 p-3">
      <div className="kostra-data-label text-[10px]">{label}</div>
      <div className="font-mono-num text-lg font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MetaCell({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-[#FDF9EE] text-[#D4AF37] inline-flex items-center justify-center shrink-0">
        <Icon size={16} weight="duotone" />
      </div>
      <div className="min-w-0">
        <div className="kostra-data-label">{label}</div>
        <div className="font-mono-num text-sm font-semibold text-[#0A3B24] mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}

function BigStat({ label, value, icon: Icon, accent = "#0A3B24", highlight }) {
  return (
    <div className={`kostra-card p-5 ${highlight ? "ring-2 ring-[#D4AF37]" : ""}`}>
      <div className="flex items-start justify-between">
        <span className="kostra-data-label">{label}</span>
        <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="mt-3 kostra-numeric-xl text-3xl text-[#111827]">{value}</div>
    </div>
  );
}
